import type { FilterField, FilterOption } from '@/components/common/AdvancedFilter';
import { OPTION_DETAIL_SEPARATOR } from '@/utils/filters';
import type { Assignment } from '../types/question.types';

type AreaKey = 'divisionName' | 'districtName' | 'thanaName';

function toInstituteOptions(assignments: Assignment[]): FilterOption[] {
  return assignments
    .filter((assignment) => assignment.instituteId !== null)
    .map((assignment) => {
      const details = [
        assignment.instituteCode === null ? null : String(assignment.instituteCode),
        assignment.instituteEiin,
        assignment.divisionName,
        assignment.districtName,
        assignment.thanaName,
      ]
        .filter(Boolean)
        .join(OPTION_DETAIL_SEPARATOR);

      return {
        value: String(assignment.instituteId),
        label: assignment.instituteName ?? `#${String(assignment.instituteId)}`,
        description: details,
        searchText: details,
      };
    });
}

function toAreaOptions(assignments: Assignment[], key: AreaKey): FilterOption[] {
  const names = new Set<string>();
  for (const assignment of assignments) {
    const name = assignment[key];
    if (name !== null && name !== '') names.add(name);
  }
  return [...names].sort().map((name) => ({ value: name, label: name }));
}

export function useAssignedInstituteFields(assignments: Assignment[]): FilterField[] {
  return [
    {
      key: 'instituteId',
      label: 'Select Institution',
      type: 'select',
      placeholder: 'All institutions',
      options: toInstituteOptions(assignments),
      searchable: true,
    },
    { key: 'instituteName', label: 'Institute Name', type: 'text', placeholder: 'Any name' },
    { key: 'instituteCode', label: 'Institute Code', type: 'text', placeholder: 'Any code' },
    { key: 'instituteEiin', label: 'Institute EIIN', type: 'text', placeholder: 'Any EIIN' },
    {
      key: 'divisionName',
      label: 'Division',
      type: 'select',
      placeholder: 'All divisions',
      options: toAreaOptions(assignments, 'divisionName'),
    },
    {
      key: 'districtName',
      label: 'District',
      type: 'select',
      placeholder: 'All districts',
      options: toAreaOptions(assignments, 'districtName'),
    },
    {
      key: 'thanaName',
      label: 'Thana',
      type: 'select',
      placeholder: 'All thanas',
      options: toAreaOptions(assignments, 'thanaName'),
    },
  ];
}
